// src/components/resources/tools/generators/AiChat/ChatGptThread.tsx

import type { JSX } from 'react'
import { cn } from '~/lib/utils'
import type { AiChatMessage, AiChatSettings } from './AiChat.types'
import { renderMessageContent, type MessageContentStyles } from './renderMessageContent'
import { ChatGptLogo } from './BrandLogos'
import {
  ChevronDownIcon,
  CopyIcon,
  MicIcon,
  MoreIcon,
  PlusIcon,
  RefreshIcon,
  SendArrowIcon,
  ShareIcon,
  ThumbDownIcon,
  ThumbUpIcon,
  VolumeIcon,
} from './Icons'

/**
 * Pixel-faithful recreation of the ChatGPT conversation surface (2026 web UI):
 * a slim model-switcher header, a centred ~768px transcript column with
 * right-aligned grey user bubbles and full-width assistant turns led by the
 * monochrome blossom mark, the post-response action toolbar, and the rounded
 * "Ask anything" composer pill.
 */

interface ThreadProps {
  settings: AiChatSettings
  messages: AiChatMessage[]
}

function contentStyles(dark: boolean): MessageContentStyles {
  return {
    paragraph: 'my-3 first:mt-0 last:mb-0 leading-[1.75]',
    heading: 'my-3 text-[18px] font-semibold first:mt-0',
    inlineCode: cn(
      'rounded px-1.5 py-0.5 font-mono text-[13.5px]',
      dark ? 'bg-[#2f2f2f] text-[#ececec]' : 'bg-[#ececec] text-[#0d0d0d]',
    ),
    codeBlock: cn('my-3 overflow-hidden rounded-2xl text-[#e6edf3]', dark ? 'bg-[#0d0d0d]' : 'bg-[#161616]'),
    codeBlockHeader: 'bg-black/30 px-4 py-1.5 text-[12px] font-medium text-white/60',
    bulletMarker: dark ? 'bg-[#ececec]' : 'bg-[#0d0d0d]',
    orderedMarker: 'font-medium',
    strong: 'font-semibold',
    link: 'text-[#0066ff] underline decoration-1 underline-offset-2',
  }
}

// Precompute both themes once at module load — the styles are static per
// theme, so there's no need to rebuild the object (and its cn() joins) on
// every render.
const CONTENT_STYLES = { light: contentStyles(false), dark: contentStyles(true) } as const

const ACTION_ICONS = [CopyIcon, ThumbUpIcon, ThumbDownIcon, VolumeIcon, RefreshIcon, ShareIcon]

export function ChatGptThread({ settings, messages }: ThreadProps): JSX.Element {
  const dark = settings.theme === 'dark'
  const styles = CONTENT_STYLES[settings.theme]

  const canvas = dark ? 'bg-[#212121] text-[#ececec]' : 'bg-white text-[#0d0d0d]'
  const userBubble = dark ? 'bg-[#303030] text-[#ececec]' : 'bg-[#f4f4f4] text-[#0d0d0d]'
  const muted = dark ? 'text-[#afafaf]' : 'text-[#8f8f8f]'
  const composer = dark ? 'bg-[#303030]' : 'bg-[#f4f4f4]'

  return (
    <div className={cn('flex h-full flex-col font-sans antialiased', canvas)} style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif' }}>
      {settings.showHeader ? (
        <header className={cn('flex items-center justify-between px-4 py-3', dark ? 'text-[#ececec]' : 'text-[#0d0d0d]')}>
          {/* Decorative mock chrome — a <div>, not a <button>, so keyboard/SR
              users don't tab into a handler-less control in the live tool. */}
          <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[17px] font-semibold">
            <span>{settings.modelLabel}</span>
            <ChevronDownIcon className={cn('h-4 w-4', muted)} />
          </div>
          <div className="flex items-center gap-2">
            <ShareIcon className={cn('h-[18px] w-[18px]', muted)} />
            {settings.showAvatars ? (
              <span className={cn('flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-semibold text-white', settings.userAvatarColor)}>
                {settings.userInitials}
              </span>
            ) : null}
          </div>
        </header>
      ) : null}

      <div className="flex-1 overflow-y-auto px-4 pb-2 pt-2">
        <div className="mx-auto flex w-full max-w-[768px] flex-col gap-1">
          {messages.map(message =>
            message.role === 'user' ? (
              <div key={message.id} className="flex justify-end py-2.5">
                <div className={cn('max-w-[75%] whitespace-pre-wrap break-words rounded-[1.5rem] px-5 py-2.5 text-[16px] leading-[1.6]', userBubble)}>
                  {message.content}
                </div>
              </div>
            ) : (
              <div key={message.id} className="flex gap-4 py-4">
                {settings.showAvatars ? (
                  <div className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border', dark ? 'border-[#4d4d4d]' : 'border-[#e3e3e3]')}>
                    <ChatGptLogo className={cn('h-[19px] w-[19px]', dark ? 'text-white' : 'text-black')} />
                  </div>
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className="text-[16px]">{renderMessageContent(message.content, styles)}</div>
                  {settings.showActions ? (
                    <div className={cn('mt-1 flex items-center gap-1', muted)}>
                      {ACTION_ICONS.map((Icon, index) => (
                        <span key={index} className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-black/5">
                          <Icon className="h-[17px] w-[17px]" />
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ),
          )}
        </div>
      </div>

      <div className="px-4 pb-4 pt-1">
        <div className="mx-auto w-full max-w-[768px]">
          <div className={cn('flex items-center gap-2 rounded-[1.75rem] px-3 py-2.5', composer)}>
            <span className={cn('flex h-9 w-9 items-center justify-center rounded-full', dark ? 'text-[#ececec] hover:bg-white/10' : 'text-[#0d0d0d] hover:bg-black/5')}>
              <PlusIcon className="h-5 w-5" />
            </span>
            <span className={cn('flex-1 px-1 text-[16px]', muted)}>Ask anything</span>
            <span className={cn('flex h-9 w-9 items-center justify-center rounded-full', muted)}>
              <MoreIcon className="h-5 w-5" />
            </span>
            <span className={cn('flex h-9 w-9 items-center justify-center rounded-full', muted)}>
              <MicIcon className="h-5 w-5" />
            </span>
            <span className={cn('flex h-9 w-9 items-center justify-center rounded-full', dark ? 'bg-white text-black' : 'bg-black text-white')}>
              <SendArrowIcon className="h-[18px] w-[18px]" strokeWidth={2} />
            </span>
          </div>
          <p className={cn('mt-2 text-center text-[12px]', muted)}>ChatGPT can make mistakes. Check important info.</p>
        </div>
      </div>
    </div>
  )
}
